import React from 'react';
import { Phone, MapPin, Clock, Instagram, Facebook } from 'lucide-react';
import { restaurantInfo, branches } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer id="contact" className="bg-stone-950 pt-20 pb-8 relative overflow-hidden">
      {/* Decorative Top Border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-amber-600 to-transparent" />

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d97706' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <a
              href="#hero"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('#hero');
              }}
              className="inline-block mb-6"
            >
              <span className="font-serif text-3xl font-bold text-amber-100">
                Köfteci Sadullah
              </span>
              <span className="block text-amber-500/60 text-xs tracking-[0.3em] uppercase mt-1">
                {restaurantInfo.since}'den beri
              </span>
            </a>
            <p className="text-amber-200/50 text-sm leading-relaxed mb-6">
              {restaurantInfo.description}
            </p>
            {/* Social Links */}
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/koftecisadullah/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-amber-600/10 rounded-lg flex items-center justify-center text-amber-500 hover:bg-amber-600 hover:text-stone-950 transition-all duration-300"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.facebook.com/koftecisadullahusta/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-amber-600/10 rounded-lg flex items-center justify-center text-amber-500 hover:bg-amber-600 hover:text-stone-950 transition-all duration-300"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg text-amber-50 mb-6">Hızlı Erişim</h4>
            <ul className="space-y-3">
              {[
                { name: 'Ana Sayfa', href: '#hero' },
                { name: 'Menü', href: '#menu' },
                { name: 'Hakkımızda', href: '#about' },
                { name: 'Şubelerimiz', href: '#locations' }
              ].map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(link.href);
                    }}
                    className="text-amber-200/60 hover:text-amber-400 transition-colors text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Branch 1 */}
          <div>
            <h4 className="font-serif text-lg text-amber-50 mb-6">Ana Şube</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-amber-500 mt-1 flex-shrink-0" />
                <span className="text-amber-200/60 text-sm">{branches[0].address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-amber-500 flex-shrink-0" />
                <a
                  href={`tel:${branches[0].phone}`}
                  className="text-amber-200/60 hover:text-amber-400 transition-colors text-sm"
                >
                  {branches[0].phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
                <span className="text-amber-200/60 text-sm">{branches[0].hours}</span>
              </li>
            </ul>
          </div>

          {/* Branch 2 */}
          <div>
            <h4 className="font-serif text-lg text-amber-50 mb-6">Avlu Şube</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-amber-500 mt-1 flex-shrink-0" />
                <span className="text-amber-200/60 text-sm">{branches[1].address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-amber-500 flex-shrink-0" />
                <a
                  href={`tel:${branches[1].phone}`}
                  className="text-amber-200/60 hover:text-amber-400 transition-colors text-sm"
                >
                  {branches[1].phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
                <span className="text-amber-200/60 text-sm">{branches[1].hours}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-amber-900/20">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-amber-200/40 text-sm">
              © {currentYear} Köfteci Sadullah. Tüm hakları saklıdır.
            </p>
            <p className="text-amber-200/40 text-sm">
              Balıkesir'in Lezzet Ustası
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;